import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";

const supported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const standalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

function toBytes(v) {
  const s = atob((v + "=".repeat((4 - (v.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(s, (c) => c.charCodeAt(0));
}

async function currentSub() {
  const reg = await navigator.serviceWorker.getRegistration("/");
  return reg ? reg.pushManager.getSubscription() : null;
}

export const PushCard = () => {
  const [state, setState] = useState("loading");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supported()) { setState(isIOS() && !standalone() ? "ios-install" : "unsupported"); return; }
    if (Notification.permission === "denied") { setState("denied"); return; }
    currentSub().then((s) => setState(s ? "on" : "off")).catch(() => setState("off"));
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { setState(perm === "denied" ? "denied" : "off"); return; }
      const reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
      await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/public-key");
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: toBytes(data.public_key) });
      const j = sub.toJSON();
      await api.post("/push/subscribe", { endpoint: j.endpoint, keys: j.keys, device: navigator.userAgent });
      const { data: t } = await api.post("/push/test");
      setState("on");
      t.sent > 0 ? toast.success("Notifiche attivate: ti è arrivato un messaggio di prova") : toast.warning("Notifiche attivate, ma la prova non è stata consegnata. Riprova tra poco.");
    } catch (e) {
      toast.error(e?.response ? errMsg(e) : "Impossibile attivare le notifiche su questo dispositivo. Controlla i permessi del telefono e riprova.");
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const s = await currentSub();
      if (s) { await api.post("/push/unsubscribe", { endpoint: s.endpoint }); await s.unsubscribe(); }
      setState("off");
      toast.success("Notifiche disattivate su questo telefono");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    try {
      const { data } = await api.post("/push/test");
      data.sent > 0 ? toast.success("Notifica di prova inviata") : toast.warning("Nessun dispositivo raggiunto. Disattiva e riattiva le notifiche.");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="card-soft mb-4 space-y-3 p-5" data-testid="push-card">
      <h3 className="flex items-center gap-2 font-bold"><BellRing className="h-4 w-4 text-primary" />Notifiche sul telefono</h3>
      <p className="text-sm text-muted-foreground" data-testid="push-status">{MSG[state]}</p>
      {state === "off" && <Button onClick={enable} disabled={busy} className="rounded-xl font-bold" data-testid="push-enable-button">Attiva notifiche su questo dispositivo</Button>}
      {state === "on" && (
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={test} className="rounded-xl" data-testid="push-test-button">Invia notifica di prova</Button>
          <Button variant="ghost" onClick={disable} disabled={busy} className="rounded-xl" data-testid="push-disable-button">Disattiva</Button>
        </div>
      )}
    </div>
  );
};

const MSG = {
  loading: "Verifica in corso…",
  off: "Ricevi le notifiche della campanella anche a telefono bloccato e con l'app chiusa.",
  on: "Attive su questo dispositivo. Le notifiche arrivano anche a schermo bloccato.",
  denied: "Hai bloccato le notifiche per DR APP. Riattivale dalle impostazioni del telefono (Impostazioni → Notifiche → DR APP) e ricarica la pagina.",
  "ios-install": "Su iPhone prima aggiungi DR APP alla schermata Home: in Safari tocca Condividi → «Aggiungi alla schermata Home», poi apri l'app dall'icona e torna qui.",
  unsupported: "Questo browser non supporta le notifiche. Su Android usa Chrome; su iPhone serve iOS 16.4 o successivo con l'app aggiunta alla schermata Home.",
};
